import { supporterUpcomingTokenExpiryDetailQuery, supporterUpcomingTokenExpirySummaryQuery } from "@/api/reporting";
import { DataTable } from "@/components/data-table/DataTable";
import { DataTableBody } from "@/components/data-table/DataTableBody";
import { StyledLink } from "@/components/link/StyledLink";
import { PointsAmount } from "@/components/PointsAmount";
import { ButtonBar } from "@/components/ui/ButtonBar";
import { CopyToClipboardButton } from "@/components/ui/CopyToClipboardButton";
import { PagePending } from "@/components/ui/PagePending";
import { PageWrapper } from "@/components/ui/PageWrapper";
import { Tooltip } from "@/components/ui/Tooltip";
import { ALL_COUNTRIES, ALL_TOKEN_CURRENCIES, COUNTRY_DATA } from "@/constants/countries";
import { countryCodeToTokenCurrencyCode, formatTokensAsMoney, formatTokensAsPoints } from "@/modules/money-fns";
import { addMonths } from "@/utils/date";
import { getMonthName, toDateOnly } from "@common/utils/date";
import { Box, Flex, Select, Span, Text } from "@givenwell/components";
import { WarningTriangleIcon } from "@givenwell/core-solid-icons";
import { UpcomingExpiration, UpcomingExpirationDetail } from "@givenwell/management-api";
import { colors } from "@givenwell/theme";
import { useQueries, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import dayjs from "dayjs";
import { useMemo } from "react";
import { fallback, object, optional, picklist, string } from "valibot";

export const Route = createFileRoute("/admin/expiry")({
  pendingComponent: PagePending,
  component: UpcomingTokenExpiryReportPage,
  validateSearch: object({
    supporter_id: optional(string()),
    expiry_date: optional(string()),
    cohort_date: optional(string()),
    months: fallback(picklist([1, 3, 6, 12]), 3),
    group_by: fallback(picklist(["expiry", "cohort"]), "cohort"),
    country: fallback(string(), ALL_COUNTRIES),
  }),
  beforeLoad: () => ({
    breadcrumb: "Points Expiry",
  }),
});

type UpcomingExpirationPlusPlus = UpcomingExpiration & {
  is_month_header?: boolean;
};
type UpcomingExpirationDetailPlusPlus = UpcomingExpirationDetail & {
  member_name: string;
  kind?: "member" | "others" | "total";
  /**
   * Unique React key for the row. A member can hold several expirations within the one group (which
   * is why the summary counts *distinct* members), so member_id + expiration is not unique.
   */
  row_key: string;
};

function UpcomingTokenExpiryReportPage() {
  const {
    supporter_id: supporterId,
    expiry_date: expiryDate,
    cohort_date: cohortDate,
    months,
    group_by: groupBy,
    country: countryCode,
  } = Route.useSearch();
  const nav = Route.useNavigate();

  // The upcoming-expirations endpoint takes a single token currency, so the
  // "all countries" view fans out one summary request per currency and merges them.
  const singleTokenCurrency = countryCode === ALL_COUNTRIES ? undefined : countryCodeToTokenCurrencyCode(countryCode);
  const tokenCurrencies = singleTokenCurrency ? [singleTokenCurrency] : ALL_TOKEN_CURRENCIES;
  const maxDate = toDateOnly(addMonths(new Date(), months));
  const summaryQuery = useQueries({
    queries: tokenCurrencies.map(tokenCurrency => supporterUpcomingTokenExpirySummaryQuery(tokenCurrency, maxDate)),
    combine: results => ({
      data: results.every(r => r.data !== undefined) ? results.flatMap(r => r.data ?? []) : undefined,
      status: results.some(r => r.status === "error")
        ? ("error" as const)
        : results.some(r => r.status === "pending")
          ? ("pending" as const)
          : ("success" as const),
    }),
  });

  const selectedSummary = useMemo(() => {
    if (!supporterId || !expiryDate || !summaryQuery.data) return null;
    return summaryQuery.data.find(item => {
      const matchesBase = item.supporter_id === supporterId && item.expiration === expiryDate;
      if (groupBy === "cohort" && cohortDate) {
        return matchesBase && item.cohort_date === cohortDate;
      }
      return matchesBase;
    });
  }, [supporterId, expiryDate, cohortDate, groupBy, summaryQuery.data]);

  // In all-countries mode the detail currency isn't known until the selected
  // summary row (which carries its own currency) has loaded.
  const detailTokenCurrency = singleTokenCurrency ?? selectedSummary?.token_currency_code;
  const detailQuery = useQuery(
    supporterUpcomingTokenExpiryDetailQuery(detailTokenCurrency, supporterId, expiryDate, cohortDate),
  );

  const [groupedData, maxTotalMembers] = useMemo((): [UpcomingExpirationPlusPlus[], number] => {
    if (!summaryQuery.data) return [[], 0];

    const grouped: UpcomingExpirationPlusPlus[] = [];
    const thisYear = new Date().getFullYear();
    let lastMonth = -1;
    let lastYear = -1;
    let maxTotalMembers = 0;

    // Sort data based on groupBy mode
    const sortedData = [...summaryQuery.data].sort((a, b) => {
      if (groupBy === "cohort") {
        // Sort by cohort_date, then expiration, then supporter_name
        const cohortCompare = a.cohort_date.localeCompare(b.cohort_date);
        if (cohortCompare !== 0) return cohortCompare;
        const expiryCompare = a.expiration.localeCompare(b.expiration);
        if (expiryCompare !== 0) return expiryCompare;
        return a.supporter_name.localeCompare(b.supporter_name);
      } else {
        // Default: sort by expiration, then supporter_name. The API returns each
        // currency's summaries sorted, but merged all-countries data needs re-sorting.
        const expiryCompare = a.expiration.localeCompare(b.expiration);
        if (expiryCompare !== 0) return expiryCompare;
        return a.supporter_name.localeCompare(b.supporter_name);
      }
    });

    for (const item of sortedData) {
      const dateForGrouping = groupBy === "cohort" ? new Date(item.cohort_date) : new Date(item.expiration);
      if (dateForGrouping.getMonth() !== lastMonth || dateForGrouping.getFullYear() !== lastYear) {
        grouped.push({
          supporter_name: `${getMonthName(dateForGrouping.getMonth())} ${
            dateForGrouping.getFullYear() == thisYear ? "" : dateForGrouping.getFullYear()
          }`,
          is_month_header: true,
        } as UpcomingExpirationPlusPlus);
        lastMonth = dateForGrouping.getMonth();
        lastYear = dateForGrouping.getFullYear();
      }
      if (item.total_member_count > maxTotalMembers) maxTotalMembers = item.total_member_count;
      grouped.push(item);
    }
    return [grouped, maxTotalMembers];
  }, [summaryQuery.data, groupBy]);

  const totalDigits = useMemo(
    () => (maxTotalMembers > 0 ? Math.ceil(Math.log10(maxTotalMembers)) + 1 : 1),
    [maxTotalMembers],
  );

  const memberData = useMemo((): UpcomingExpirationDetailPlusPlus[] => {
    if (!detailQuery.data || !selectedSummary || detailQuery.data.length == 0) return [];

    let tokensTotal = 0;
    let spentTotal = 0;

    for (const item of detailQuery.data) {
      tokensTotal += item.token_amount;
      spentTotal += item.spent_amount;
    }

    const memberTableData: UpcomingExpirationDetailPlusPlus[] = [
      ...detailQuery.data.map((item, index) => ({
        ...item,
        kind: "member" as const,
        row_key: `member-${index}-${item.member_id}`,
      })),
    ];

    let amountOthersSpent = 0;
    if (selectedSummary.total_member_count > 0) {
      const otherCount = selectedSummary.total_member_count - selectedSummary.affected_member_count;
      if (otherCount > 0) {
        amountOthersSpent = selectedSummary.total_token_amount - selectedSummary.spent_amount;
        const otherMembersRow: UpcomingExpirationDetailPlusPlus = {
          expiration: selectedSummary.expiration || "",
          member_id: "",
          member: null!,
          member_name: `${otherCount} other member${otherCount === 1 ? "" : "s"}`,
          spent_amount: amountOthersSpent,
          token_amount: amountOthersSpent,
          token_currency_code: selectedSummary.token_currency_code,
          kind: "others",
          row_key: "others",
          wallet_balance: 0,
        };
        memberTableData.push(otherMembersRow);
      }
    }

    const totalRow: UpcomingExpirationDetailPlusPlus = {
      expiration: selectedSummary.expiration || "",
      member_id: "",
      member_name: "TOTAL",
      member: null!,
      spent_amount: spentTotal + amountOthersSpent,
      token_amount: tokensTotal + amountOthersSpent,
      token_currency_code: selectedSummary.token_currency_code,
      kind: "total",
      row_key: "total",
      wallet_balance: 0,
    };
    memberTableData.push(totalRow);

    return memberTableData;
  }, [detailQuery.data, selectedSummary]);

  const showMemberDetail = !!selectedSummary;

  function handleCreateCsv() {
    if (!selectedSummary) return "";
    const csvRows = [
      [
        "Email",
        "First Name",
        "Last Name",
        "Account Created",
        "Expiring Points",
        "Wallet Balance",
        "MemberId",
        "Supporter",
      ].join("\t"),
      ...(detailQuery.data ?? []).map(detail => {
        const firstName = detail.member?.given_name || "Unknown";
        const lastName = detail.member?.family_name || "Unknown";
        const email = detail.member?.email || "Unknown";
        const expiring = tokensToPoints(detail.token_amount - detail.spent_amount);
        const wallet = tokensToPoints(detail.wallet_balance);
        return [
          email,
          firstName,
          lastName,
          detail.member.account_created,
          expiring,
          wallet,
          detail.member.id,
          selectedSummary.supporter_name,
        ].join("\t");
      }),
    ];
    return csvRows.join("\n");
  }

  function tokensToPoints(amount: number) {
    if (!detailTokenCurrency) return "";
    return formatTokensAsMoney({ amount, tokenCurrencyCode: detailTokenCurrency });
  }

  function tokensToCurrency(amount: number) {
    if (!detailTokenCurrency) return "";
    return formatTokensAsMoney({ amount, tokenCurrencyCode: detailTokenCurrency });
  }

  function handleMonthsChanged(newMonths: "1" | "3" | "6" | "12" | undefined): void {
    nav({
      to: "/admin/expiry",
      search: {
        months: (newMonths !== undefined ? +newMonths : 3) as 1 | 3 | 6 | 12,
        supporter_id: supporterId,
        expiry_date: expiryDate,
        cohort_date: cohortDate,
        group_by: groupBy,
        country: countryCode,
      },
    });
  }

  function handleGroupByChanged(newGroupBy: "expiry" | "cohort" | undefined): void {
    nav({
      to: "/admin/expiry",
      search: {
        months,
        group_by: newGroupBy ?? "expiry",
        country: countryCode,
        // Clear selection when switching modes
        supporter_id: undefined,
        expiry_date: undefined,
        cohort_date: undefined,
      },
    });
  }

  function handleCountryChanged(newCountry: string): void {
    nav({
      to: "/admin/expiry",
      search: {
        months,
        group_by: groupBy,
        country: newCountry,
        // Clear selection when switching countries
        supporter_id: undefined,
        expiry_date: undefined,
        cohort_date: undefined,
      },
    });
  }

  return (
    <PageWrapper title="Expiring Points">
      <Flex css={{ flexDir: "column", gap: 12 }}>
        <Flex css={{ gap: 24, alignItems: "center" }}>
          <ButtonBar
            names={["1 month", "3 months", "6 months", "12 months"]}
            options={["1", "3", "6", "12"]}
            onChange={handleMonthsChanged}
            value={String(months) as any}
            includeAll={false}
          />
          <ButtonBar
            names={["By Cohort", "By Expiry Date"]}
            options={["cohort", "expiry"]}
            onChange={handleGroupByChanged}
            value={groupBy}
            includeAll={false}
          />
          <Select css={{ width: 200 }} size="sm" value={countryCode} onValueChange={handleCountryChanged}>
            <option value={ALL_COUNTRIES}>All countries</option>
            {Object.entries(COUNTRY_DATA).map(([code, country]) => (
              <option key={code} value={code}>
                {country.name}
              </option>
            ))}
          </Select>
        </Flex>
        <Flex css={{ flexDir: "row", gap: 24 }}>
          <DataTable fitContent css={{ pt: 0 }}>
            <DataTableBody
              data={groupedData ?? []}
              dataKey={r =>
                `${r.supporter_id}-${r.expiration}-${r.cohort_date}-${r.token_currency_code}-${r.supporter_name}`
              }
              status={summaryQuery.status}
              getSelected={r => r.is_month_header ?? false}
              hideHeader
              containerCss={{ pt: 0 }}
              rounded="full"
              columns={[
                {
                  header: "",
                  cell: r =>
                    r.is_month_header ? (
                      <Box css={{ fontWeight: 600, fontScale: "sm", color: colors.black }}>{r.supporter_name}</Box>
                    ) : (
                      <StyledLink
                        to={`/admin/expiry`}
                        search={{
                          supporter_id: r.supporter_id,
                          expiry_date: r.expiration,
                          cohort_date: groupBy === "cohort" ? r.cohort_date : undefined,
                          months: months,
                          group_by: groupBy,
                          country: countryCode,
                        }}
                        css={{ pl: 12 }}
                      >
                        {r.supporter_name}
                      </StyledLink>
                    ),
                  css: { minWidth: 250 },
                },
                {
                  header: "",
                  cell: row =>
                    row.is_month_header ? (
                      <Span css={{ fontScale: "xs", color: colors.text }}>Expiry Date</Span>
                    ) : (
                      dayjs(row.expiration).format("DD MMMM")
                    ),
                  css: { color: colors.text2, pr: 24 },
                },
                // {
                //   header: " ",
                //   cell: row => {
                //     if (row.is_month_header)
                //       return <Span css={{ fontScale: "xs", color: colors.text }}>Unspent Points</Span>;
                //     const percent =
                //       (row.token_amount - row.spent_amount) /
                //       ((row.token_amount / row.affected_member_count) * row.total_member_count);
                //     const highPercent = percent > 0.5;

                //     return (
                //       <>
                //         <Span
                //           css={{ color: highPercent ? colors.red500 : colors.text, fontWeight: highPercent ? 600 : 400 }}
                //         >
                //           {numericFormat.format(row.token_amount - row.spent_amount)}{" "}
                //         </Span>
                //         {row.total_member_count > 0 && (
                //           <>
                //             <Span
                //               css={{
                //                 color: highPercent ? colors.red500 : colors.text2,
                //               }}
                //             >
                //               /{" "}
                //               {numericFormat.format(
                //                 (row.token_amount / row.affected_member_count) * row.total_member_count,
                //               )}
                //             </Span>
                //           </>
                //         )}
                //       </>
                //     );
                //   },
                //   css: { textAlign: "ight" },
                // },
                {
                  header: "",
                  cell: row => {
                    if (row.is_month_header)
                      return <Span css={{ fontScale: "xs", color: colors.text }}>Unspent $ vs. Allowance</Span>;
                    const percent =
                      (row.token_amount - row.spent_amount) /
                      ((row.token_amount / row.affected_member_count) * row.total_member_count);
                    const highPercent = percent > 0.5;

                    return (
                      <Span>
                        <Span css={{ color: highPercent ? colors.red500 : colors.text, fontWeight: 500 }}>
                          {formatTokensAsMoney(
                            { amount: row.token_amount - row.spent_amount, tokenCurrencyCode: row.token_currency_code },
                            { minimumFractionDigits: 0, maximumFractionDigits: 0 },
                          )}{" "}
                        </Span>
                        {row.total_member_count > 0 && (
                          <Span css={{ color: highPercent ? colors.red500 : colors.text2 }}>
                            /{" "}
                            {formatTokensAsPoints(
                              {
                                amount: Math.round(
                                  (row.token_amount / row.affected_member_count) * row.total_member_count,
                                ),
                                tokenCurrencyCode: row.token_currency_code,
                              },
                              { minimumFractionDigits: 0, maximumFractionDigits: 0 },
                            )}
                          </Span>
                        )}
                      </Span>
                    );
                  },
                  css: { textAlign: "right", pl: 36 },
                },
                {
                  header: "",
                  cell: row => {
                    if (row.is_month_header)
                      return <Span css={{ fontScale: "xs", color: colors.text }}>= &nbsp;%</Span>;
                    const percent =
                      (row.token_amount - row.spent_amount) /
                      ((row.token_amount / row.affected_member_count) * row.total_member_count);
                    const highPercent = percent > 0.5;

                    return (
                      <>
                        {row.total_member_count > 0 && (
                          <>
                            <Span css={{ fontWeight: 500, color: highPercent ? colors.red500 : colors.text }}>
                              = &nbsp;{(100 * percent).toFixed(0)}%
                            </Span>
                          </>
                        )}
                      </>
                    );
                  },
                  textAlign: "center",
                  css: { textAlign: "left", pl: 0 },
                },
                // {
                //   header: " Unspent $",
                //   cell: row => (row.is_month_header ? null : formatCents(20 * (row.token_amount - row.spent_amount))),
                //   css: { textAlign: "right", minW: 100 },
                // },
                {
                  header: "",
                  cell: row => {
                    if (row.is_month_header)
                      return <Span css={{ fontScale: "xs", color: colors.text, pl: 6 }}># Members</Span>;

                    const percent = row.total_member_count > 0 ? row.affected_member_count / row.total_member_count : 0;
                    const highPercent = percent > 0.5;
                    return (
                      <>
                        <Span css={{ fontWeight: 600, color: highPercent ? colors.red500 : colors.text }}>
                          {row.affected_member_count}
                        </Span>
                        <Span
                          css={{
                            display: "inline-block",
                            width: 4 + totalDigits * 8,
                            color: highPercent ? colors.red500 : colors.text2,
                          }}
                        >
                          {row.total_member_count > 0 ? <>/ {row.total_member_count}</> : <>/ ??</>}
                        </Span>{" "}
                        <Span css={{ color: highPercent ? colors.red500 : colors.text2, opacity: Math.ceil(percent) }}>
                          mbrs
                        </Span>
                      </>
                    );
                  },
                  css: { textAlign: "right", pl: 30 },
                },
              ]}
            />
          </DataTable>
          {/* ================== */}

          {showMemberDetail && (
            <Box>
              <Text css={{ mb: 12 }}>
                <StyledLink
                  to="/supporter/$supporterId/team"
                  params={{ supporterId: selectedSummary?.supporter_id }}
                  target="_blank"
                  css={{ fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 1 }}
                >
                  {selectedSummary?.supporter_name}
                </StyledLink>{" "}
                points expiring on{" "}
                <Span css={{ fontWeight: 600 }}>{dayjs(selectedSummary?.expiration).format("DD MMMM YYYY")}</Span>
                <CopyToClipboardButton value={handleCreateCsv} what="member data" />
              </Text>
              <DataTable>
                <DataTableBody
                  data={memberData}
                  dataKey={r => r.row_key}
                  status={detailQuery.status}
                  rounded="full"
                  columns={[
                    {
                      header: "Member",
                      cell: r => {
                        if (r.kind === "total") {
                          return <Span css={{ fontWeight: 600 }}>TOTAL</Span>;
                        }
                        if (r.kind === "others") {
                          return <Span css={{ color: colors.text2 }}>{r.member_name}</Span>;
                        }
                        if (!r.member?.account_created) {
                          return (
                            <Tooltip
                              side="left"
                              style={{ width: "fit-content" }}
                              content={<Span css={{ whiteSpace: "nowrap" }}>Member account not created</Span>}
                            >
                              <Span css={{ color: colors.text2, display: "inline-flex", gap: 4, alignItems: "center" }}>
                                {r.member?.given_name}&nbsp;{r.member?.family_name}&nbsp;
                                <WarningTriangleIcon opacity={0.5} strokeWidth={0.5} width={12} height={12} />
                              </Span>
                            </Tooltip>
                          );
                        }
                        return (
                          <StyledLink to="/admin/members/$memberId" params={{ memberId: r.member.id }}>
                            {r.member?.given_name} {r.member?.family_name}
                          </StyledLink>
                        );
                      },
                      css: { maxWidth: 250, overflow: "hidden", textOverflow: "ellipsis", pr: 24 },
                    },
                    {
                      header: "Allowance",
                      cell: r => (
                        <Span
                          css={{
                            fontWeight: r.kind === "total" ? 600 : undefined,
                            color: r.kind === "others" ? colors.text2 : undefined,
                          }}
                        >
                          <PointsAmount amount={r.token_amount} tokenCurrency={r.token_currency_code} />
                        </Span>
                      ),
                      css: { textAlign: "right", width: 80 },
                    },
                    {
                      header: "Spent",
                      cell: r => (
                        <Span
                          css={{
                            fontWeight: r.kind === "total" ? 600 : undefined,
                            color: r.kind === "others" ? colors.text2 : undefined,
                          }}
                        >
                          {r.spent_amount === 0 ? (
                            ""
                          ) : (
                            <PointsAmount amount={r.spent_amount} tokenCurrency={r.token_currency_code} />
                          )}
                        </Span>
                      ),
                      css: { textAlign: "right", width: 80 },
                    },
                    {
                      header: " = % ",
                      cell: r => {
                        if (r.kind === "total") return null;
                        return (
                          <>
                            ={" "}
                            <Span css={{ color: colors.text2 }}>
                              {((r.spent_amount / r.token_amount) * 100).toFixed(0)}%
                            </Span>
                          </>
                        );
                      },
                      css: { textAlign: "left" },
                    },
                    {
                      header: "Wallet",
                      cell: r =>
                        r.kind === "member" ? (
                          <PointsAmount amount={r.wallet_balance} tokenCurrency={r.token_currency_code} />
                        ) : (
                          ""
                        ),
                      css: { textAlign: "right" },
                    },
                    {
                      header: "Expiring $",
                      cell: r => (
                        <Span css={{ fontWeight: r.kind === "total" ? 600 : undefined }}>
                          {r.kind === "member" || r.kind === "total" ? (
                            <>{tokensToCurrency(r.token_amount - r.spent_amount)}</>
                          ) : null}
                        </Span>
                      ),
                      css: { textAlign: "right", pl: 36 },
                    },
                  ]}
                />
              </DataTable>
            </Box>
          )}
        </Flex>
      </Flex>
    </PageWrapper>
  );
}
